import {
	currencyFormatter,
	currencySymbol,
	type NumberFormatBaseOptions,
	normalizeArrayWithCurrency,
	numberFormat,
	numberFormatterManualDigits,
} from '@enity/formatting'
import i18n from '@libs/internationalization'
import type { PointLabelObject } from 'highcharts'
import { Interval } from 'luxon'
import { ComparisonEnum, type ConsumptionType, type Rotation, type RotationType } from '../interfaces/interfaces'

const standardOptions = (value: number) =>
	({
		maximumFractionDigits: value ? (String(Math.round(value)).length === 1 ? 1 : 0) : 0,
		minimumFractionDigits: value ? (String(Math.round(value)).length === 1 ? 1 : 0) : 0,
		notation: value >= 1e6 ? 'compact' : 'standard',
	}) as NumberFormatBaseOptions

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

export const fluidPixels = (minPx: number, maxPx: number, minViewport = 768, maxViewport = 3840): string => {
	const viewportWidth = typeof window === 'undefined' ? maxViewport : window.innerWidth
	const ratio = clamp((viewportWidth - minViewport) / (maxViewport - minViewport), 0, 1)
	return `${Math.round(minPx + (maxPx - minPx) * ratio)}px`
}

export const chartFontSizes = () => ({
	axisLabel: fluidPixels(18, 24),
	dataLabel: fluidPixels(18, 30),
	legend: fluidPixels(16, 25),
	subtitle: fluidPixels(22, 38),
	title: fluidPixels(38, 64),
	yAxisLabel: fluidPixels(16, 25),
})

// Formats the displayed number in chart
export const formatter = (value: number, unit = ''): string => {
	const internalFormatter = numberFormatterManualDigits(i18n.global.locale, standardOptions(value))

	return internalFormatter(value) + ' ' + unit
}

export const formatCurrency = (unit: string, value: number) => {
	return currencyFormatter(i18n.global.locale, unit, value, standardOptions(value))
}

// @ts-expect-error
export function formatAxisNumber(this: PointLabelObject, options) {
	const value = this.y as number
	const point = this.point as PointLabelObject['point'] & {
		unit: string;
		options: { consumptionType: ConsumptionType; comparison: ComparisonEnum };
	}
	if (point.options.comparison === ComparisonEnum.periodically) {
		options.verticalAlign = 'top'
		options.y = -10
	}
	return point.options.consumptionType === 'cost' ? formatCurrency(point.unit, value) : formatter(value, point.unit)
}

// Preliminary transformer that change how the data is structured, if you want to add any kind of data to the point object, do it here as it (probably) won't be available elsewhere
export const standardDataTransformer = (data: Rotation, dataLabel: 'currentData' | 'comparisonDeltaData') => {
	const dataArr = data?.chart?.[dataLabel]

	return dataArr
		? dataArr.map((value: number) => ({
			comparison: data.comparison,
			consumptionType: data.consumptionType,
			unit: data.unit,
			y: value !== 0 && value >= 0 ? value : null,
		}))
		: []
}

export const formatCategories = (
	categories: string[],
	type: 'translation' | 'prefixWeek' | string,
	resolution: string
): string[] => {
	return categories.map((category) => {
		if (type === 'translation' && (resolution === 'P1D' || resolution === 'P1Y')) {
			return i18n.global.t(`sharedTexts.time.${category}`)
		}

		if (type === 'prefixWeek') {
			return i18n.global.t('sharedTexts.time.week', { week: category })
		}

		if (type === 'dateRange') {
			return category
		}

		return i18n.global.t(`sharedTexts.time.${category}`) ?? category
	})
}

// Something something to get like ['2020', '2021'] for legend
export const formatLegends = (data: Rotation) => {
	if (data.comparison !== ComparisonEnum.periodically) return []
	if (data.resolution === 'PT1H') {
		return [i18n.global.t('sharedTexts.time.SameDayWeekBefore'), i18n.global.t('sharedTexts.time.CurrentDay')]
	}
	if (data.chart.comparisonPartialData?.some((value) => value)) {
		const compYearStart = Interval.fromISO(data.chart.categories.comparisonFullInterval)?.start.year
		const compYearEnd = Interval.fromISO(data.chart.categories.comparisonFullInterval)?.end.year
		const actualYearStart = Interval.fromISO(data.chart.categories.interval)?.start.year
		const actualYearEnd = Interval.fromISO(data.chart.categories.interval)?.end.year
		const compYear = compYearStart !== compYearEnd ? `${compYearStart} - ${compYearEnd}` : compYearStart
		const actualYear = actualYearStart !== actualYearEnd ? `${actualYearStart} - ${actualYearEnd}` : actualYearStart
		return [compYear, actualYear]
	}
	const actualYearStart = Interval.fromISO(data.chart.categories.interval)?.start.year
	const actualYearEnd = Interval.fromISO(data.chart.categories.interval)?.end.year
	const actualYear = actualYearStart !== actualYearEnd ? `${actualYearStart} - ${actualYearEnd}` : actualYearStart
	return [actualYear]
}

export const formatTitle = (
	rotationType: RotationType,
	energyType: string | string[],
	consumptionType: ConsumptionType
) =>
	rotationType === 'single'
		? i18n.global.t(`sharedTexts.energyTypes.${energyType}`)
		: i18n.global.t(`sharedTexts.energyTypes.${rotationType}.${consumptionType}`)

export const formatSubTitle = (data: Rotation, locale: string) => {
	if (data.rotationType === 'single') {
		if (data.period === 'month') {
			if (data.energyType === 'chargingPoint') {
				return i18n.global.t(`sharedTexts.comparisons.${data.comparison}.${data.resolution}.chargingPoint`, {
					count: data.chart.currentData.length,
				})
			}
			return i18n.global.t(`sharedTexts.comparisons.${data.comparison}.${data.resolution}.month`, {
				count: data.chart.currentData.length,
				unit: data.consumptionType === 'cost' ? currencySymbol(locale, data.unit, { display: 'symbol' }) : data.unit,
			})
		}
		return i18n.global.t(
			`sharedTexts.comparisons.${data.comparison}.${data.resolution}.${data.isProduction ? 'production' : 'consumption'}`,
			{
				count: data.chart.currentData.length,
				unit: data.consumptionType === 'cost' ? currencySymbol(locale, data.unit, { display: 'symbol' }) : data.unit,
			}
		)
	}
	return i18n.global.t(`sharedTexts.time.${data.resolution}`)
}

// Collapse me :)
export const findPeriod = (data: Rotation): string => {
	if (data.period === 'month') {
		return i18n.global.t('sharedTexts.time.TheLastXDays', { days: data.chart.currentData.length }).toLowerCase()
	}
	if (data.comparison === ComparisonEnum.yearToDate) {
		return i18n.global.t('sharedTexts.time.yearToDate')
	}
	if (data.comparison === ComparisonEnum.periodically) {
		if (data.resolution === 'PT1H') return i18n.global.t('sharedTexts.time.CurrentDay').toLowerCase()
		if (data.resolution === 'P1D') return i18n.global.t('sharedTexts.time.TheLast7Days').toLowerCase()
		if (data.resolution === 'P1W') return i18n.global.t('sharedTexts.time.ThePeriod').toLowerCase()
		if (data.resolution === 'P1M') return i18n.global.t('sharedTexts.time.TheLast12Months').toLowerCase()
		return i18n.global.t('sharedTexts.time.ThePeriod').toLowerCase()
	}
	if (data.comparison === ComparisonEnum.budget) {
		return i18n.global.t('sharedTexts.time.CurrentMonth').toLowerCase()
	}
	if (data.comparison === ComparisonEnum.accumulated) {
		if (data.resolution === 'P1D') return i18n.global.t('sharedTexts.time.CurrentDay').toLowerCase()
		if (data.resolution === 'P1W') return i18n.global.t('sharedTexts.time.TheLast7Days').toLowerCase()
		if (data.resolution === 'P1M') return i18n.global.t('sharedTexts.time.TheLast30Days').toLowerCase()
		if (data.resolution === 'P1Y') return i18n.global.t('sharedTexts.time.TheLast12Months').toLowerCase()
		return i18n.global.t('sharedTexts.time.ThePeriod').toLowerCase()
	}
	return i18n.global.t('sharedTexts.time.ThePeriod').toLowerCase()
}

export const findTheConsumptionTypeText = (consumptionType: ConsumptionType) => {
	return i18n.global.t(`sharedTexts.theConsumptionTypeText.${consumptionType}`)
}

export const findConsumptionTypeText = (consumptionType: ConsumptionType) => {
	return i18n.global.t(`sharedTexts.consumptionTypeText.${consumptionType}`)
}

export const formatEqualTo = (rotation: Rotation) => {
	return i18n.global.tc(
		`sharedTexts.relatesTo.${rotation.isProduction ? 'production' : 'consumption'}.${rotation.description.equalTo.type}`,
		rotation.description.equalTo.value === 1 ? 1 : 2,
		{
			amount:
				rotation.description.equalTo.value > 10
					? numberFormat(i18n.global.locale, rotation.description.equalTo.value)
					: rotation.description.equalTo.value,
			consumptionTypeText: findConsumptionTypeText(rotation.consumptionType),
			period: findPeriod(rotation),
			theConsumptionTypeText: findTheConsumptionTypeText(rotation.consumptionType),
		}
	)
}

export const formatCurrencyUnitToHighest = (locale: string, unit: string, values: number[]) =>
	normalizeArrayWithCurrency(locale, unit, values)

export default {
	formatAxisNumber,
	formatCategories,
	formatter,
	standardDataTransformer,
}
